import React, { useRef, useEffect } from 'react';

/**
 * GenerateQuestionPanel Component
 * Floating panel for configuring question generation parameters
 * 
 * @param {boolean} isOpen - Whether the panel is open
 * @param {function} onClose - Close handler
 * @param {Object} questionData - Question configuration data
 * @param {function} onQuestionDataChange - Handler for question data changes
 * @param {function} onGenerate - Handler for generate button click
 * @param {boolean} isGenerating - Whether generation is in progress
 */
const GenerateQuestionPanel = ({
  isOpen,
  onClose,
  questionData,
  onQuestionDataChange,
  onGenerate,
  isGenerating
}) => {
  const panelRef = useRef(null);

  // Close panel when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        // Check if click is on the generate button (parent trigger)
        const isGenerateButton = event.target.closest('button')?.textContent?.includes('Generate');
        if (!isGenerateButton) {
          onClose();
        }
      }
    };

    // Add slight delay to prevent immediate closing
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      ref={panelRef}
      className="absolute top-full right-0 mt-2 w-[600px] bg-white border-2 rounded-lg p-6 space-y-6 shadow-2xl z-[100]"
      style={{ borderColor: 'var(--color-brand-blue)' }}
    >
      {/* Breadth, Seniority, and Max Time in 3 columns */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            Complexity (Size)
          </label>
          <select
            value={questionData.breadth}
            onChange={(e) => onQuestionDataChange({ ...questionData, breadth: e.target.value })}
            className="select-custom w-full py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-white"
          >
            <option value="Focused">Simple</option>
            <option value="Involved">Medium</option>
            <option value="Comprehensive">Complex</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            Difficulty
          </label>
          <select
            value={questionData.seniority}
            onChange={(e) => onQuestionDataChange({ ...questionData, seniority: e.target.value })}
            className="select-custom w-full py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-white"
          >
            <option value="Junior">Junior</option>
            <option value="Middle">Middle</option>
            <option value="Senior">Senior</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            Max Time Min (To solve)
          </label>
          <input
            type="number"
            value={questionData.maxTime}
            onChange={(e) => onQuestionDataChange({ ...questionData, maxTime: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-white"
            min="1"
            max="60"
          />
        </div>
      </div>

      {/* Further Guidance */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          Further Guidance (optional)
        </label>
        <textarea
          value={questionData.furtherGuidance}
          onChange={(e) => onQuestionDataChange({ ...questionData, furtherGuidance: e.target.value })}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-white"
          placeholder="Add any specific guidance for generating this question..."
        />
      </div>

      {/* Generate Button */}
      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={onGenerate}
          disabled={isGenerating}
          className="px-8 py-3 text-white font-semibold rounded-lg hover:opacity-90 transition-colors flex items-center gap-2 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: 'var(--color-brand-blue)' }}
        >
          {isGenerating ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Generating...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Generate Question
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default GenerateQuestionPanel;
